import React, { useState, useRef, useEffect } from "react";
import CommonButton from "../CommonButton";
import {
  Camera,
  CheckCircle,
  ChevronDown,
  PawPrint,
  X,
  Gift,
  Calendar,
  Scale,
  AlertTriangle,
  Layers,
  Plus,
  CalendarDays,
  Sparkles,
  Trophy,
  ArrowRight,
  Info,
} from "lucide-react";
import TextInputComp from "./TextInputComp";
import { Option } from "./Dropdown";
import {
  activityLevels,
  breedOptionsMap,
  colorOptions,
  dietaryOptions,
  dietfrequecy,
  favouriteTreatOptions,
  genderOptions,
  groomingfrequecyOptions,
  personalityOptions,
  petTypes,
  protionSize,
  sheddingfrequecyOptions,
  suggestedConditions,
} from "../../utils/Customer/CustomerData";
import TabPills from "./TabPills";
import CardWrapper from "./CardWrapper";
import { ICreatePetProfileRequest } from "../../types/Request/CustomerRequest";
import {
  useCreateUserProfileMutation,
  useUpdatePetProfileMutation,
  useLazyGetPetProfileByIdQuery,
  useUploadCustomerFileMutation,
} from "../../redux/actions/Customer/profileCustomer";
import { PetData } from "../../types/Response/CustomerResponse";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export interface StepCompletionMap {
  isBasicDetailsComplete: boolean;
  isFoodPreferencesComplete: boolean;
  isHealthConditionsComplete: boolean;
  isGroomingSheddingComplete: boolean;
}

type Props = {
  goTo?: (screen: string) => void;
  idFromRoute?: number;
};

export const getGenderString = (gender: number): string => {
  switch (gender) {
    case 1:
      return "Male";
    case 2:
      return "Female";
    default:
      return "";
  }
};

export const getGenderNumber = (gender: string): number => {
  switch (gender.toLowerCase()) {
    case "male":
      return 1;
    case "female":
      return 2;
    default:
      return 0;
  }
};

export const getPetTypeString = (petType: number): string => {
  switch (petType) {
    case 1:
      return "dog";
    case 2:
      return "cat";
    default:
      return "";
  }
};

export const getPetTypeNumber = (petType: string): number => {
  switch (petType.toLowerCase()) {
    case "dog":
      return 1;
    case "cat":
      return 2;
    default:
      return 0;
  }
};

const AddPetProfileUI: React.FC<Props> = ({ goTo, idFromRoute }) => {
  const [fetchPetProfile, { isFetching: isProfileLoading }] =
    useLazyGetPetProfileByIdQuery();
  const [createPetProfile, { isLoading: isCreating }] =
    useCreateUserProfileMutation();
  const [updatePetProfile, { isLoading: isUpdating }] =
    useUpdatePetProfileMutation();
  const [uploadFile, { isLoading: isUploading }] = useUploadCustomerFileMutation();

  const [petId, setPetId] = useState<number | null>(idFromRoute || null);
  // Track if this registration instance started purely as a new profile additions flow
  const isNewPetRegistration = !idFromRoute;

  const [completedStepsStatus, setCompletedStepsStatus] =
    useState<StepCompletionMap>({
      isBasicDetailsComplete: false,
      isFoodPreferencesComplete: false,
      isHealthConditionsComplete: false,
      isGroomingSheddingComplete: false,
    });

  const [activeStepIndex, setActiveStepIndex] = useState<number>(1);
  const [points, setPoints] = useState<number>(0);

  // Celebratory Firecracker Modal States
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebratedPoints, setCelebratedPoints] = useState(0);

  const step2Ref = useRef<HTMLDivElement>(null);
  const step3Ref = useRef<HTMLDivElement>(null);
  const step4Ref = useRef<HTMLDivElement>(null);

  // --- FORM STATES ---
  const [petImage, setPetImage] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState("cat");
  const [genderInput, setGenderInput] = useState("");
  const [petData, setPetData] = useState<PetData>();
  const [selectedEmoji, setSelectedEmoji] = useState(petTypes[0].image);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [petName, setPetName] = useState("");
  const [weight, setWeight] = useState(0);
  const [dateType, setDateType] = useState<string>("birthday");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [activity, setActivity] = useState("2");
  const [openDropdown, setOpenDropdown] = useState<
    "breed" | "gender" | "color" | null
  >(null);
  const [breedInput, setBreedInput] = useState("");
  const [selectedBreed, setSelectedBreed] = useState<Option | null>(null);
  const [selectedGender, setSelectedGender] = useState<Option | null>(null);
  const [colorInput, setColorInput] = useState("");
  const [selectedColor, setSelectedColor] = useState<Option | null>(null);
  const [localPersonalities, setLocalPersonalities] =
    useState(personalityOptions);
  const [personality, setPersonality] = useState<string[]>([]);
  const [customPersonalityInput, setCustomPersonalityInput] = useState("");
  const [localDietaryOptions, setLocalDietaryOptions] =
    useState(dietaryOptions);
  const [dietary, setDietary] = useState<string[]>([]);
  const [customDietaryInput, setCustomDietaryInput] = useState("");
  const [localTreatOptions, setLocalTreatOptions] = useState(
    favouriteTreatOptions
  );
  const [favouriteTreats, setFavouriteTreats] = useState<string[]>([]);
  const [customTreatInput, setCustomTreatInput] = useState("");
  const [dietFrequency, setDietFrequency] = useState("2");
  const [portionSize, setPortionSize] = useState("1");
  const [localHealthConditions, setLocalHealthConditions] = useState<
    Array<{ id: string; label: string }>
  >(suggestedConditions.map((item) => ({ id: item.id, label: item.label })));
  const [healthSearch, setHealthSearch] = useState("");
  const [healthConditions, setHealthConditions] = useState<string[]>([]);
  const [noIssues, setNoIssues] = useState(false);
  const [groomingfrequecy, setGroomingfrequecy] = useState("2");
  const [customGroomingMonths, setCustomGroomingMonths] = useState("1");
  const [sheddingfrequecy, setSheddingfrequecy] = useState("2");
  const [breedSize, setBreedSize] = useState<
    "Small" | "Medium" | "Large" | "Extra Large"
  >("Medium");

  const isNetworkBusy = isCreating || isUpdating || isProfileLoading;

  const fetchApi = async () => {
    if (idFromRoute) {
      try {
        const response = await fetchPetProfile(idFromRoute).unwrap();
        if (response?.data) {
          setPetData(response.data);
        }
      } catch (err) {
        console.error("Failed to load initialized pet context query:", err);
      }
    }
  };

  useEffect(() => {
    if (idFromRoute) {
      fetchApi();
    }
  }, [idFromRoute]);

  useEffect(() => {
    if (petData) {
      const data = petData;

      setCompletedStepsStatus({
        isBasicDetailsComplete: data.isBasicDetailsComplete ?? false,
        isFoodPreferencesComplete: data.isFoodPreferencesComplete ?? false,
        isHealthConditionsComplete: data.isHealthConditionsComplete ?? false,
        isGroomingSheddingComplete: data.isGroomingSheddingComplete ?? false,
      });

      setPetName(data.petName || "");
      setSelectedType(getPetTypeString(data.petType) || "cat");
      setGenderInput(getGenderString(data.gender) || "");
      setWeight(data.weight || 0);
      setDateType(data.dateType || "birthday");
      setSelectedDate(data.petDate ? new Date(data.petDate) : null);
      setBreedInput(data.breed || "");
      setColorInput(data.furColor || "");
      setActivity(data.activityLevel || "2");
      setPetImage(data.photoUrl || null);

      if (data.personality) {
        const resolvedIds: string[] = [];
        const customItemsToAdd: typeof personalityOptions = [];
        data.personality.forEach((valStr) => {
          const matched = personalityOptions.find(
            (o) => o.label.toLowerCase() === valStr.toLowerCase()
          );
          if (matched) {
            resolvedIds.push(matched.id);
          } else {
            const customId = `custom_p_${Math.random()
              .toString(36)
              .substr(2, 9)}`;
            customItemsToAdd.push({
              id: customId,
              label: valStr,
              value: customId,
              icon: PawPrint,
            });
            resolvedIds.push(customId);
          }
        });
        if (customItemsToAdd.length > 0)
          setLocalPersonalities([...personalityOptions, ...customItemsToAdd]);
        setPersonality(resolvedIds);
      }

      if (data.foodPreferences) {
        const resolvedIds: string[] = [];
        const customItemsToAdd: typeof dietaryOptions = [];
        data.foodPreferences.forEach((valStr) => {
          const matched = dietaryOptions.find(
            (o) => o.label.toLowerCase() === valStr.toLowerCase()
          );
          if (matched) {
            resolvedIds.push(matched.id);
          } else {
            const customId = `custom_d_${Math.random()
              .toString(36)
              .substr(2, 9)}`;
            customItemsToAdd.push({
              id: customId,
              label: valStr,
              value: customId,
            });
            resolvedIds.push(customId);
          }
        });
        if (customItemsToAdd.length > 0)
          setLocalDietaryOptions([...dietaryOptions, ...customItemsToAdd]);
        setDietary(resolvedIds);
      }

      setDietFrequency(data.dietFrequency || "2");
      setPortionSize(data.portionSize || "1");

      if (data.favouriteTreats) {
        const resolvedIds: string[] = [];
        const customItemsToAdd: typeof favouriteTreatOptions = [];
        data.favouriteTreats.forEach((valStr) => {
          const matched = favouriteTreatOptions.find(
            (o) => o.label.toLowerCase() === valStr.toLowerCase()
          );
          if (matched) {
            resolvedIds.push(matched.id);
          } else {
            const customId = `custom_t_${Math.random()
              .toString(36)
              .substr(2, 9)}`;
            customItemsToAdd.push({
              id: customId,
              label: valStr,
              value: customId,
            });
            resolvedIds.push(customId);
          }
        });
        if (customItemsToAdd.length > 0)
          setLocalTreatOptions([...favouriteTreatOptions, ...customItemsToAdd]);
        setFavouriteTreats(resolvedIds);
      }

      setNoIssues(data.noKnownIssues ?? false);
      if (data.healthConditions) {
        const baseConditions = suggestedConditions.map((item) => ({
          id: item.id,
          label: item.label,
        }));
        const resolvedIds: string[] = [];
        const customItemsToAdd: Array<{ id: string; label: string }> = [];
        data.healthConditions.forEach((valStr) => {
          const matched = baseConditions.find(
            (o) => o.label.toLowerCase() === valStr.toLowerCase()
          );
          if (matched) {
            resolvedIds.push(matched.id);
          } else {
            const customId = `custom_h_${Math.random()
              .toString(36)
              .substr(2, 9)}`;
            customItemsToAdd.push({ id: customId, label: valStr });
            resolvedIds.push(customId);
          }
        });
        setLocalHealthConditions([...baseConditions, ...customItemsToAdd]);
        setHealthConditions(resolvedIds);
      }

      setSheddingfrequecy(data.sheddingFrequency || "2");
      setBreedSize((data.breedSize as any) || "Medium");
      if (data.groomingFrequency?.startsWith("Custom:")) {
        setGroomingfrequecy("3");
        const matchedMonth = data.groomingFrequency.match(/\d+/);
        if (matchedMonth) setCustomGroomingMonths(matchedMonth[0]);
      } else {
        setGroomingfrequecy(data.groomingFrequency || "2");
      }
    }
  }, [petData]);

  const rawBreeds = breedOptionsMap[selectedType as "dog" | "cat"] || [];
  const filteredBreeds = rawBreeds.filter((b) =>
    b.label.toLowerCase().includes(breedInput.toLowerCase())
  );
  const filteredGenders = genderOptions.filter((g) =>
    g.label.toLowerCase().includes(genderInput)
  );
  const filteredColors = colorOptions.filter((c) =>
    c.label.toLowerCase().includes(colorInput.toLowerCase())
  );
  const customGroomingOptions = groomingfrequecyOptions.map((opt) =>
    opt.label.toLowerCase() === "rare"
      ? { ...opt, label: "Custom Interval" }
      : opt
  );

  const hasAllergySelected = healthConditions.some((id) => {
    const matchingObj = localHealthConditions.find((o) => o.id === id);
    const resolvedText = matchingObj ? matchingObj.label : id;
    return (
      resolvedText.toLowerCase().includes("allergy") ||
      resolvedText.toLowerCase().includes("issue")
    );
  });

  useEffect(() => {
    let calculatedPoints = 0;
    if (completedStepsStatus.isBasicDetailsComplete) calculatedPoints += 25;
    if (completedStepsStatus.isFoodPreferencesComplete) calculatedPoints += 25;
    if (completedStepsStatus.isHealthConditionsComplete) calculatedPoints += 25;
    if (completedStepsStatus.isGroomingSheddingComplete) calculatedPoints += 25;
    setPoints(calculatedPoints);

    if (!completedStepsStatus.isBasicDetailsComplete) {
      setActiveStepIndex(1);
    } else if (!completedStepsStatus.isFoodPreferencesComplete) {
      setActiveStepIndex(2);
      scrollToRef(step2Ref);
    } else if (!completedStepsStatus.isHealthConditionsComplete) {
      setActiveStepIndex(3);
      scrollToRef(step3Ref);
    } else if (!completedStepsStatus.isGroomingSheddingComplete) {
      setActiveStepIndex(4);
      scrollToRef(step4Ref);
    } else {
      setActiveStepIndex(4);
    }
  }, [completedStepsStatus]);

  useEffect(() => {
    if (!petData) {
      setBreedInput("");
      setSelectedBreed(null);
    }
  }, [selectedType]);

  useEffect(() => {
    const handleOutsideClick = () => setOpenDropdown(null);
    window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, []);

  const scrollToRef = (ref: React.RefObject<HTMLDivElement | null>) => {
    setTimeout(() => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);
  };

  const toggleChipSelection = (
    id: string,
    state: string[],
    setState: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    state.includes(id)
      ? setState(state.filter((item) => item !== id))
      : setState([...state, id]);
  };

  const handleAddCustomPersonality = () => {
    const value = customPersonalityInput.trim();
    if (!value) return;
    const newId = `custom_p_${Date.now()}`;
    setLocalPersonalities((prev) => [
      ...prev,
      { id: newId, label: value, value: newId, icon: PawPrint },
    ]);
    setPersonality((prev) => [...prev, newId]);
    setCustomPersonalityInput("");
  };
  const handleAddSuggestedHealth = (item: { id: string; label: string }) => {
    setHealthConditions(
      (prev) =>
        prev.includes(item.id)
          ? prev.filter((id) => id !== item.id) // remove if already selected
          : [...prev, item.id] // add if not selected
    );

    if (noIssues) setNoIssues(false);
  };

  const handleAddCustomDietary = () => {
    const value = customDietaryInput.trim();
    if (!value) return;
    const newId = `custom_d_${Date.now()}`;
    setLocalDietaryOptions((prev) => [
      ...prev,
      { id: newId, label: value, value: newId },
    ]);
    setDietary((prev) => [...prev, newId]);
    setCustomDietaryInput("");
  };

  const removeCondition = (id: string) => {
    setHealthConditions((prev) => prev.filter((i) => i !== id));
  };

  const handleAddCustomTreat = () => {
    const value = customTreatInput.trim();
    if (!value) return;
    const newId = `custom_t_${Date.now()}`;
    setLocalTreatOptions((prev) => [
      ...prev,
      { id: newId, label: value, value: newId },
    ]);
    setFavouriteTreats((prev) => [...prev, newId]);
    setCustomTreatInput("");
  };

  const handleAddCustomHealth = () => {
    const text = healthSearch.trim();
    if (!text) return;
    const targetId = `custom_h_${Date.now()}`;
    if (healthConditions.includes(targetId)) return;
    setLocalHealthConditions((prev) => [
      ...prev,
      { id: targetId, label: text },
    ]);
    setHealthConditions((prev) => [...prev, targetId]);
    setHealthSearch("");
    if (noIssues) setNoIssues(false);
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const response = await uploadFile({
        File: file,
        FolderName: "Customers/Avatars",
      }).unwrap();

      setPetImage(response.fileUrl);
    } catch (error) {
      console.error(
        "API error mapping uploaded profile media path handle:",
        error
      );
      alert("Profile image upload failed. Please try again.");
    }
  };

  const handleSaveStep = async (stepNumber: number) => {
    const resolvedPersonalities = personality.map(
      (id) => localPersonalities.find((o) => o.id === id)?.label || id
    );
    const resolvedDietary = dietary.map(
      (id) => localDietaryOptions.find((o) => o.id === id)?.label || id
    );
    const resolvedTreats = favouriteTreats.map(
      (id) => localTreatOptions.find((o) => o.id === id)?.label || id
    );
    const resolvedHealthTexts = healthConditions.map(
      (id) => localHealthConditions.find((o) => o.id === id)?.label || id
    );
    const finalGroomingLabel =
      groomingfrequecy === "3"
        ? `Custom: Every ${customGroomingMonths} Months`
        : groomingfrequecy;

    let payload: ICreatePetProfileRequest = {
      currentSubmittedStep: stepNumber,
      petType: getPetTypeNumber(selectedType),
      isFullyFilled: stepNumber === 4,
      petName: petName,
      breed: selectedBreed?.value || breedInput,
      gender: getGenderNumber(selectedGender?.value || genderInput),
      weight: Number(weight),
      dateType: dateType,
      petDate: selectedDate ? selectedDate.toISOString() : "",
      furColor: selectedColor?.value || colorInput,
      activityLevel: activity,
      personality: resolvedPersonalities,
      foodPreferences: resolvedDietary,
      dietFrequency: dietFrequency,
      portionSize: portionSize,
      favouriteTreats: resolvedTreats,
      noKnownIssues: noIssues,
      healthConditions: resolvedHealthTexts,
      requiresVetConsultationAlert: hasAllergySelected,
      groomingFrequency: finalGroomingLabel,
      sheddingFrequency: sheddingfrequecy,
      breedSize: breedSize,
      photoUrl: petImage || "",
    };

    try {
      let backendResponse;
      if (!petId) {
        backendResponse = await createPetProfile(payload).unwrap();
        if (backendResponse?.data?.id) {
          setPetId(backendResponse.data.id);
        }
      } else {
        backendResponse = await updatePetProfile({
          petId: petId,
          data: payload,
        }).unwrap();
      }

      setCompletedStepsStatus((prev) => {
        const nextStatus = { ...prev };
        if (stepNumber === 1) nextStatus.isBasicDetailsComplete = true;
        if (stepNumber === 2) nextStatus.isFoodPreferencesComplete = true;
        if (stepNumber === 3) nextStatus.isHealthConditionsComplete = true;
        if (stepNumber === 4) nextStatus.isGroomingSheddingComplete = true;
        return nextStatus;
      });

      // Fire Celebration effect only if creating a brand new pet instance profile
      if (isNewPetRegistration) {
        setCelebratedPoints(stepNumber * 25);
        setShowCelebration(true);
      } else if (stepNumber === 4) {
        if (goTo) goTo("dashboard");
      }
    } catch (error) {
      console.error("Internal fault during save operation:", error);
      alert("Something went wrong saving this step.");
    }
  };

  const handleDismissCelebration = () => {
    setShowCelebration(false);
    if (
      activeStepIndex === 4 &&
      completedStepsStatus.isGroomingSheddingComplete
    ) {
      if (goTo) goTo("dashboard");
    }
  };

  if (isProfileLoading && !petData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F2]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2F5E2E] mx-auto mb-4"></div>
          <p className="text-[#2F5E2E] font-bold">Syncing profile metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F2] rounded-3xl p-2 sm:p-4 selection:bg-[#A8D97B]/30 relative">
      {/* --- IMMERSIVE FIRECRACKER CELEBRATION MODAL COMPONENT --- */}
      {showCelebration && (
        <div className="fixed inset-0 bg-[#2F5E2E]/80 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full text-center shadow-2xl relative overflow-hidden transform scale-100 transition-all border border-[#A8D97B]/30">
            {/* Visual background sparkles circles */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-20">
              <div className="absolute top-12 left-12 w-24 h-24 rounded-full bg-amber-400 blur-xl animate-pulse" />
              <div className="absolute bottom-12 right-12 w-32 h-32 rounded-full bg-[#6DBE45] blur-xl animate-pulse" />
            </div>

            {/* Animated Firecracker / Badge Rings */}
            <div className="w-20 h-20 bg-gradient-to-tr from-amber-400 via-orange-400 to-red-500 rounded-full flex items-center justify-center mx-auto shadow-lg relative animate-bounce">
              <Trophy className="text-white" size={36} />
              <Sparkles
                className="absolute -top-1 -right-1 text-yellow-300 animate-spin"
                size={20}
              />
              <Sparkles
                className="absolute -bottom-2 -left-2 text-yellow-200 animate-ping"
                size={16}
              />
            </div>

            <h2 className="text-2xl font-black text-[#2F5E2E] mt-5 tracking-tight">
              Step Unlocked! 🎇
            </h2>
            <p className="text-gray-600 text-sm mt-1.5 font-medium">
              You are doing fantastic! Profile progress updated successfully.
            </p>

            <div className="my-6 bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 border border-orange-200 p-4 rounded-2xl shadow-xs">
              <p className="text-xs font-bold text-orange-700 uppercase tracking-widest">
                Rewards Score Added
              </p>
              <p className="text-4xl font-black text-orange-600 mt-1 animate-pulse">
                +25 PTS
              </p>
              <div className="w-full bg-gray-200 h-2 rounded-full mt-3 overflow-hidden p-0.5">
                <div
                  className="bg-orange-500 h-full rounded-full transition-all duration-500"
                  style={{ width: `${celebratedPoints}%` }}
                />
              </div>
              <p className="text-[11px] font-semibold text-gray-500 mt-2">
                Total Balance: {celebratedPoints} / 100 Points
              </p>
            </div>

            <button
              type="button"
              onClick={handleDismissCelebration}
              className="w-full bg-[#2F5E2E] hover:bg-[#1f421e] text-white font-bold py-3 px-6 rounded-xl shadow-md flex items-center justify-center gap-2 transition"
            >
              {celebratedPoints === 100
                ? "Finish Registration 🏆"
                : "Proceed to Next Section"}{" "}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="relative w-full bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-[#A8D97B]/40 p-4 sm:p-6 z-10 max-w-5xl mx-auto">
        {/* --- HEADER DASHBOARD AND SCORE ENGINE TRACKER --- */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-100/80 pb-4 mb-6 gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-[#2F5E2E] tracking-tight">
              {isNewPetRegistration
                ? "Create Pet Identity Passport"
                : "Edit Pet Settings UI"}
            </h1>
            <p className="text-xs text-gray-500 font-medium">
              Progress saved securely. Pick up right where you left off!
            </p>
          </div>
          {isNewPetRegistration && (
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200/60 px-4 py-2 rounded-2xl flex items-center gap-2.5 self-stretch sm:self-auto shadow-sm">
              <Gift className="text-orange-500 animate-bounce" size={18} />
              <div>
                <p className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">
                  Earned Bonus
                </p>
                <p className="text-md sm:text-lg font-black text-orange-600">
                  {points} / 100 PTS
                </p>
              </div>
            </div>
          )}
        </div>

        {/* --- VISUAL INTERACTIVE PROGRESS TIMELINE MAP GUIDE --- */}
        <div className="mb-8 grid grid-cols-2 md:grid-cols-4 gap-3 bg-gray-50 p-3 rounded-2xl border border-gray-200/60">
          <button
            type="button"
            onClick={() => setActiveStepIndex(1)}
            className={`p-2.5 text-left rounded-xl transition-all border ${
              activeStepIndex === 1
                ? "bg-white border-[#A8D97B] shadow-xs"
                : "border-transparent opacity-80"
            }`}
          >
            <div className="flex items-center gap-1.5 font-bold text-xs text-gray-700">
              {completedStepsStatus.isBasicDetailsComplete ? (
                <CheckCircle size={14} className="text-emerald-600" />
              ) : (
                <div className="w-3.5 h-3.5 rounded-full border border-gray-400 flex items-center justify-center text-[9px]">
                  1
                </div>
              )}
              Basic Profile
            </div>
            <span className="text-[10px] text-gray-400 font-medium block mt-0.5">
              {completedStepsStatus.isBasicDetailsComplete
                ? "Completed"
                : "Pending Action"}
            </span>
          </button>

          <button
            type="button"
            disabled={!completedStepsStatus.isBasicDetailsComplete}
            onClick={() => setActiveStepIndex(2)}
            className={`p-2.5 text-left rounded-xl transition-all border disabled:opacity-40 disabled:cursor-not-allowed ${
              activeStepIndex === 2
                ? "bg-white border-[#A8D97B] shadow-xs"
                : "border-transparent opacity-80"
            }`}
          >
            <div className="flex items-center gap-1.5 font-bold text-xs text-gray-700">
              {completedStepsStatus.isFoodPreferencesComplete ? (
                <CheckCircle size={14} className="text-emerald-600" />
              ) : (
                <div className="w-3.5 h-3.5 rounded-full border border-gray-400 flex items-center justify-center text-[9px]">
                  2
                </div>
              )}
              Dietary Setup
            </div>
            <span className="text-[10px] text-gray-400 font-medium block mt-0.5">
              {completedStepsStatus.isFoodPreferencesComplete
                ? "Completed"
                : "Pending Action"}
            </span>
          </button>

          <button
            type="button"
            disabled={!completedStepsStatus.isFoodPreferencesComplete}
            onClick={() => setActiveStepIndex(3)}
            className={`p-2.5 text-left rounded-xl transition-all border disabled:opacity-40 disabled:cursor-not-allowed ${
              activeStepIndex === 3
                ? "bg-white border-[#A8D97B] shadow-xs"
                : "border-transparent opacity-80"
            }`}
          >
            <div className="flex items-center gap-1.5 font-bold text-xs text-gray-700">
              {completedStepsStatus.isHealthConditionsComplete ? (
                <CheckCircle size={14} className="text-emerald-600" />
              ) : (
                <div className="w-3.5 h-3.5 rounded-full border border-gray-400 flex items-center justify-center text-[9px]">
                  3
                </div>
              )}
              Health Metrics
            </div>
            <span className="text-[10px] text-gray-400 font-medium block mt-0.5">
              {completedStepsStatus.isHealthConditionsComplete
                ? "Completed"
                : "Pending Action"}
            </span>
          </button>

          <button
            type="button"
            disabled={!completedStepsStatus.isHealthConditionsComplete}
            onClick={() => setActiveStepIndex(4)}
            className={`p-2.5 text-left rounded-xl transition-all border disabled:opacity-40 disabled:cursor-not-allowed ${
              activeStepIndex === 4
                ? "bg-white border-[#A8D97B] shadow-xs"
                : "border-transparent opacity-80"
            }`}
          >
            <div className="flex items-center gap-1.5 font-bold text-xs text-gray-700">
              {completedStepsStatus.isGroomingSheddingComplete ? (
                <CheckCircle size={14} className="text-emerald-600" />
              ) : (
                <div className="w-3.5 h-3.5 rounded-full border border-gray-400 flex items-center justify-center text-[9px]">
                  4
                </div>
              )}
              Grooming Meta
            </div>
            <span className="text-[10px] text-gray-400 font-medium block mt-0.5">
              {completedStepsStatus.isGroomingSheddingComplete
                ? "Completed"
                : "Pending Action"}
            </span>
          </button>
        </div>

        {/* --- SECTION 1: PET DETAILS --- */}
        {activeStepIndex === 1 && (
          <div className="mb-6 transition-all duration-300 animate-fadeIn space-y-4">
            <CardWrapper
              title="1. Basic Pet Profile Details"
              isOpen={true}
              onClick={() => {}}
            >
              <div className="flex flex-col md:flex-row gap-6 mb-6">
                <div className="flex flex-col items-center w-full md:w-[35%]">
                  <div
                    className="p-1 rounded-2xl bg-gradient-to-br from-[#A8D97B] to-[#6DBE45] shadow-md hover:scale-[1.02] transition-transform duration-200"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="w-full h-40 rounded-xl bg-[#F4F4F2] flex items-center justify-center overflow-hidden cursor-pointer">
                      <img
                        src={petImage || selectedEmoji}
                        alt="pet profile"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  <CommonButton
                    text={isUploading ? "Uploading..." : "Change Avatar"}
                    icon={<Camera size={14} />}
                    variant="filled"
                    className="mt-2.5 px-4 py-1.5 text-xs font-semibold shadow-sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                  />
                </div>

                <div className="w-full md:w-[65%] flex flex-col justify-between gap-4">
                  <div>
                    <p className="text-[#2F5E2E] text-sm font-bold mb-2 tracking-wide">
                      Select Pet Class Type
                    </p>
                    <div className="grid grid-cols-3 gap-2.5">
                      {petTypes.map((t) => {
                        const isSelected =
                          selectedType === t.label.toLowerCase();
                        return (
                          <button
                            type="button"
                            key={t.id}
                            onClick={() => {
                              setSelectedType(t.label.toLowerCase());
                              setSelectedEmoji(t.image);
                              setSelectedBreed(null);
                              setBreedInput("");
                              setOpenDropdown(null);
                            }}
                            className={`p-2.5 rounded-xl border-2 flex flex-col items-center justify-center transition-all duration-200 ${
                              isSelected
                                ? "border-[#6DBE45] bg-[#A8D97B]/20 font-bold scale-105 shadow-sm text-[#2F5E2E]"
                                : "border-gray-200 bg-white text-gray-500"
                            }`}
                          >
                            <img
                              src={t.image}
                              className="w-8 h-8 object-contain mb-1.5"
                              alt={t.label}
                            />
                            <span className="text-[10px] tracking-tight">
                              {t.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <TextInputComp
                    showLabel={true}
                    label="Pet Name"
                    value={petName}
                    onChangeText={setPetName}
                    placeholder="Name your fur baby..."
                    className="w-full bg-white !border !border-[#A8D97B]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div
                  className="flex flex-col relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="text-md text-mainBrandColor">
                    Select Breed
                  </label>
                  <div className="flex items-center bg-white border border-[#A8D97B] rounded-xl px-3 py-2 justify-between shadow-sm focus-within:ring-2 focus-within:ring-[#6DBE45]/20">
                    <input
                      type="text"
                      className="w-full text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400"
                      placeholder="Search or type breed..."
                      value={breedInput}
                      onChange={(e) => {
                        setBreedInput(e.target.value);
                        setOpenDropdown("breed");
                      }}
                      onFocus={() => setOpenDropdown("breed")}
                    />
                    <ChevronDown
                      size={16}
                      className={`text-gray-400 cursor-pointer transition-transform duration-200 ${
                        openDropdown === "breed" ? "rotate-180" : ""
                      }`}
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === "breed" ? null : "breed"
                        )
                      }
                    />
                  </div>
                  {openDropdown === "breed" && (
                    <div className="absolute top-[100%] left-0 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto mt-1 z-50 py-1">
                      {filteredBreeds.length > 0 ? (
                        filteredBreeds.map((opt) => (
                          <div
                            key={opt.value}
                            className="px-4 py-2 text-sm hover:bg-[#A8D97B]/20 cursor-pointer text-gray-700 font-medium"
                            onClick={() => {
                              setSelectedBreed(opt);
                              setBreedInput(opt.label);
                              setOpenDropdown(null);
                            }}
                          >
                            {opt.label}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-xs text-gray-400 italic">
                          No matching options. Type to custom add.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div
                  className="flex flex-col relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="text-md text-mainBrandColor">
                    Select Gender
                  </label>
                  <div className="flex items-center bg-white border border-[#A8D97B] rounded-xl px-3 py-2 justify-between shadow-sm focus-within:ring-2 focus-within:ring-[#6DBE45]/20">
                    <input
                      type="text"
                      className="w-full text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400"
                      placeholder="Search or type gender..."
                      value={genderInput}
                      onChange={(e) => {
                        setGenderInput(e.target.value);
                        setOpenDropdown("gender");
                      }}
                      onFocus={() => setOpenDropdown("gender")}
                    />
                    <ChevronDown
                      size={16}
                      className={`text-gray-400 cursor-pointer transition-transform duration-200 ${
                        openDropdown === "gender" ? "rotate-180" : ""
                      }`}
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === "gender" ? null : "gender"
                        )
                      }
                    />
                  </div>
                  {openDropdown === "gender" && (
                    <div className="absolute top-[100%] left-0 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto mt-1 z-50 py-1">
                      {filteredGenders.map((opt) => (
                        <div
                          key={opt.value}
                          className="px-4 py-2 text-sm hover:bg-[#A8D97B]/20 cursor-pointer text-gray-700 font-medium"
                          onClick={() => {
                            setSelectedGender(opt);
                            setGenderInput(opt.label);
                            setOpenDropdown(null);
                          }}
                        >
                          {opt.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="flex flex-col">
                  <label className="text-md text-mainBrandColor">
                    Weight (KG)
                  </label>
                  <div className="flex items-center bg-white border border-[#A8D97B] rounded-xl px-3 py-2 shadow-sm">
                    <input
                      type="number"
                      step="0.1"
                      className="w-full text-sm outline-none bg-transparent text-gray-700"
                      placeholder="e.g. 6.5"
                      value={weight || ""}
                      onChange={(e) => setWeight(Number(e.target.value))}
                    />
                    <span className="text-xs font-bold text-gray-400 ml-1">
                      KG
                    </span>
                  </div>
                </div>

                <div className="flex flex-col border border-[#A8D97B] rounded-xl bg-white p-2 shadow-sm justify-between">
                  <div className="flex flex-wrap items-center justify-between gap-1 mb-1 border-b border-gray-100 pb-1">
                    <span className="text-[10px] font-extrabold text-gray-400 tracking-wider flex items-center gap-1">
                      <Calendar size={11} /> TIMELINE ASSIGNMENT:
                    </span>
                    <div className="flex bg-gray-100 rounded-lg p-0.5 text-[9px] font-bold">
                      <button
                        type="button"
                        className={`px-2 py-0.5 rounded-md transition-all ${
                          dateType === "birthday"
                            ? "bg-[#2F5E2E] text-white shadow-xs"
                            : "text-gray-500"
                        }`}
                        onClick={() => setDateType("birthday")}
                      >
                        Birthday
                      </button>
                      <button
                        type="button"
                        className={`px-2 py-0.5 rounded-md transition-all ${
                          dateType === "adoption"
                            ? "bg-[#2F5E2E] text-white shadow-xs"
                            : "text-gray-500"
                        }`}
                        onClick={() => setDateType("adoption")}
                      >
                        Adoption
                      </button>
                    </div>
                  </div>
                  <div className="relative mt-1">
                    <CalendarDays
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6DBE45] z-10"
                    />

                    <DatePicker
                      selected={selectedDate}
                      onChange={(date: Date | null) => setSelectedDate(date)}
                      dateFormat="dd MMM yyyy"
                      placeholderText={
                        dateType === "birthday"
                          ? "Select Birthday"
                          : "Select Adoption Date"
                      }
                      maxDate={new Date()}
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={100}
                      dropdownMode="select"
                      popperPlacement="bottom-start"
                      calendarClassName="custom-datepicker"
                      popperClassName="z-[9999]"
                      wrapperClassName="w-full"
                      onKeyDown={(e) => e.preventDefault()}
                      isClearable
                      customInput={
                        <input
                          readOnly
                          className="w-full h-10 rounded-lg border border-[#DDE7D8] bg-[#FAFBF8] pl-10 pr-4 text-[#2F5E2E] text-xs font-semibold shadow-sm outline-none transition-all duration-200 focus:border-[#6DBE45] cursor-pointer"
                        />
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div
                  className="flex flex-col relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="text-md text-mainBrandColor">
                    Select Fur Color
                  </label>
                  <div className="flex items-center bg-white border border-[#A8D97B] rounded-xl px-3 py-2 justify-between shadow-sm">
                    <input
                      type="text"
                      className="w-full text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400"
                      placeholder="Search or type color..."
                      value={colorInput}
                      onChange={(e) => {
                        setColorInput(e.target.value);
                        setOpenDropdown("color");
                      }}
                      onFocus={() => setOpenDropdown("color")}
                    />
                    <ChevronDown
                      size={16}
                      className={`text-gray-400 cursor-pointer transition-transform duration-200 ${
                        openDropdown === "color" ? "rotate-180" : ""
                      }`}
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === "color" ? null : "color"
                        )
                      }
                    />
                  </div>
                  {openDropdown === "color" && (
                    <div className="absolute top-[100%] left-0 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto mt-1 z-50 py-1">
                      {filteredColors.map((opt) => (
                        <div
                          key={opt.value}
                          className="px-4 py-2 text-sm hover:bg-[#A8D97B]/20 cursor-pointer text-gray-700 font-medium"
                          onClick={() => {
                            setSelectedColor(opt);
                            setColorInput(opt.label);
                            setOpenDropdown(null);
                          }}
                        >
                          {opt.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <TabPills
                  label="Activity Level"
                  options={activityLevels}
                  value={activity}
                  onChange={setActivity}
                />
              </div>

              <div className="py-3 ">
                <p className="text-md text-mainBrandColor mb-2">
                  What's their Personality like?
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {localPersonalities.map((item) => {
                    const isChecked = personality.includes(item.id);
                    return (
                      <button
                        type="button"
                        key={item.id}
                        onClick={() =>
                          toggleChipSelection(
                            item.id,
                            personality,
                            setPersonality
                          )
                        }
                        className={`px-3 py-1.5 text-xs font-semibold rounded-full border flex items-center gap-1 ${
                          isChecked
                            ? "bg-[#2F5E2E] text-white scale-105"
                            : "bg-gradient-to-r from-emerald-50 to-teal-50/60 text-emerald-800"
                        }`}
                      >
                        <PawPrint size={11} /> {item.label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-2 max-w-xs items-center bg-white p-1 rounded-xl border border-emerald-200 shadow-xs">
                  <input
                    type="text"
                    placeholder="Add custom personality..."
                    value={customPersonalityInput}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCustomPersonality();
                      }
                    }}
                    onChange={(e) => setCustomPersonalityInput(e.target.value)}
                    className="text-xs px-2.5 py-1 outline-none flex-1 text-gray-700 bg-transparent"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomPersonality}
                    className="bg-[#2F5E2E] text-white rounded-lg p-1.5"
                  >
                    <Plus size={12} />
                  </button>
                </div>
              </div>

              <div className="pt-4 flex justify-center border-t mt-5">
                <CommonButton
                  text={
                    isNetworkBusy
                      ? "Saving Details..."
                      : "Save Details & Proceed"
                  }
                  variant="filled"
                  className="w-full sm:w-[50%] py-2.5 font-bold shadow-md"
                  onClick={() => handleSaveStep(1)}
                  disabled={
                    isNetworkBusy || !petName || !weight || !selectedDate
                  }
                />
              </div>
            </CardWrapper>
          </div>
        )}

        {/* --- SECTION 2: FOOD PREFERENCE --- */}
        <div ref={step2Ref} className="scroll-mt-6">
          {activeStepIndex === 2 && (
            <div className="mb-6 transition-all duration-300 animate-fadeIn space-y-4">
              {isNewPetRegistration && (
                <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-xs text-amber-900 font-bold flex items-center gap-2 shadow-xs animate-pulse">
                  <Gift size={16} className="text-amber-600 animate-bounce" />{" "}
                  Setup Dietary and Food Menu Preferences to secure your next
                  +25 Points!
                </div>
              )}

              <CardWrapper
                title="2. Food Preference"
                isOpen={true}
                onClick={() => {}}
              >
                {completedStepsStatus.isFoodPreferencesComplete && (
                  <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-2 text-xs font-bold flex items-center gap-2">
                    <CheckCircle size={14} /> Section stored inside cloud
                    profile settings database blocks.
                  </div>
                )}
                <div className="py-3">
                  <p className="text-md text-mainBrandColor mb-2">
                    Dietary Types & Food Style
                  </p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {localDietaryOptions.map((item) => {
                      const isChecked = dietary.includes(item.id);
                      return (
                        <button
                          type="button"
                          key={item.id}
                          onClick={() =>
                            toggleChipSelection(item.id, dietary, setDietary)
                          }
                          className={`px-3 py-1.5 text-xs font-semibold rounded-full border flex items-center gap-1 ${
                            isChecked
                              ? "bg-[#2F5E2E] text-white"
                              : "bg-gradient-to-r from-emerald-50 to-teal-50/60 text-emerald-800"
                          }`}
                        >
                          <PawPrint size={11} /> {item.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 max-w-xs items-center bg-white p-1 rounded-xl border border-emerald-200 shadow-xs">
                    <input
                      type="text"
                      placeholder="Add custom meal preference..."
                      value={customDietaryInput}
                      onChange={(e) => setCustomDietaryInput(e.target.value)}
                      className="text-xs px-2.5 py-1 outline-none flex-1 text-gray-700 bg-transparent"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddCustomDietary();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomDietary}
                      className="bg-[#2F5E2E] text-white rounded-lg p-1.5"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
                  <TabPills
                    label="Feeding Frequency"
                    options={dietfrequecy}
                    value={dietFrequency}
                    onChange={setDietFrequency}
                  />
                  <TabPills
                    label="Portion Calibration"
                    options={protionSize}
                    value={portionSize}
                    onChange={setPortionSize}
                  />
                </div>

                <div className="py-3">
                  <p className="text-md text-mainBrandColor mb-2">
                    Favorite Treats & Snacks Style
                  </p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {localTreatOptions.map((item) => {
                      const isChecked = favouriteTreats.includes(item.id);
                      return (
                        <button
                          type="button"
                          key={item.id}
                          onClick={() =>
                            toggleChipSelection(
                              item.id,
                              favouriteTreats,
                              setFavouriteTreats
                            )
                          }
                          className={`px-3 py-1.5 text-xs font-semibold rounded-full border flex items-center gap-1 ${
                            isChecked
                              ? "bg-[#2F5E2E] text-white"
                              : "bg-gradient-to-r from-emerald-50 to-teal-50/60 text-emerald-800"
                          }`}
                        >
                          <PawPrint size={11} /> {item.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 max-w-xs items-center bg-white p-1 rounded-xl border border-emerald-200 shadow-xs">
                    <input
                      type="text"
                      placeholder="Add custom signature snack..."
                      value={customTreatInput}
                      onChange={(e) => setCustomTreatInput(e.target.value)}
                      className="text-xs px-2.5 py-1 outline-none flex-1 text-gray-700 bg-transparent"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddCustomTreat();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomTreat}
                      className="bg-[#2F5E2E] text-white rounded-lg p-1.5"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </div>

                <div className="pt-4 flex justify-end border-t mt-5">
                  <CommonButton
                    text={
                      isNetworkBusy ? "Processing..." : "Save & Move Forward"
                    }
                    variant="filled"
                    className="px-6 py-2 shadow-md"
                    onClick={() => handleSaveStep(2)}
                    disabled={isNetworkBusy}
                  />
                </div>
              </CardWrapper>
            </div>
          )}
        </div>

        {/* --- SECTION 3: HEALTH CONDITIONS --- */}
        <div ref={step3Ref} className="scroll-mt-6">
          {activeStepIndex === 3 && (
            <div className="mb-6 transition-all duration-300 animate-fadeIn space-y-4">
              {isNewPetRegistration && (
                <div className="bg-gradient-to-r from-red-50 via-orange-50 to-red-50 border border-orange-200 rounded-2xl px-4 py-3 text-xs text-orange-900 font-bold flex items-center gap-2 shadow-xs animate-pulse">
                  <Trophy size={16} className="text-orange-600" /> Log Health
                  Conditions and Sensitivities to gain an additional +25 Points!
                </div>
              )}

              <CardWrapper
                title="3. Health Conditions"
                isOpen={true}
                onClick={() => {}}
              >
                {completedStepsStatus.isHealthConditionsComplete && (
                  <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-2 text-xs font-bold flex items-center gap-2">
                    <CheckCircle size={14} /> Medical verification status saved.
                  </div>
                )}
                <div className="flex gap-2 mb-4 w-full items-center">
                  <div className="flex-1">
                    <TextInputComp
                      placeholder="Search or specify custom health symptom..."
                      value={healthSearch}
                      onChangeText={setHealthSearch}
                      className="!bg-white px-4 py-2 text-sm"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddCustomHealth();
                        }
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddCustomHealth}
                    className="bg-[#2F5E2E] text-white p-2.5 rounded-xl"
                  >
                    <Plus size={16} />
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                  {localHealthConditions.map((item) => {
                    const isChecked = healthConditions.includes(item.id);
                    return (
                      <button
                        type="button"
                        key={item.id}
                        onClick={() => handleAddSuggestedHealth(item)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semiBold border ${
                          isChecked
                            ? "bg-[#2F5E2E] text-white"
                            : "bg-white text-gray-600"
                        }`}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>

                {hasAllergySelected && (
                  <div className="my-4 flex items-start gap-3 bg-gradient-to-br from-amber-50 to-orange-50/60 border border-amber-200 text-amber-900 rounded-2xl p-4 text-xs font-semibold shadow-xs max-w-2xl">
                    <Info
                      className="text-amber-600 flex-shrink-0 mt-0.5"
                      size={16}
                    />
                    <div>
                      <p className="font-black text-sm text-amber-800">
                        Special Sensitivity/Allergy Detected
                      </p>
                      <p className="mt-1 font-medium text-gray-600 leading-relaxed">
                        {" "}
                        We highly recommend coordinating with a professional
                        veterinary doctor to build a specialized evaluation
                        protocol.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-2xl mb-4 min-h-[48px] items-center border border-gray-200 shadow-inner">
                  <button
                    type="button"
                    onClick={() => {
                      setNoIssues(!noIssues);
                      if (!noIssues) setHealthConditions([]);
                    }}
                    className={`px-3 py-1.5 rounded-xl text-xs font-black border ${
                      noIssues ? "bg-emerald-700 text-white" : "bg-white"
                    }`}
                  >
                    No Known Issues
                  </button>
                  {healthConditions.map((id) => {
                    const match = localHealthConditions.find(
                      (o) => o.id === id
                    );
                    return (
                      <span
                        key={id}
                        className="px-3 py-1 rounded-full text-xs font-bold bg-white text-gray-700 border border-gray-200 flex items-center gap-1.5"
                      >
                        {match ? match.label : id}
                        <X
                          size={12}
                          className="cursor-pointer text-gray-400 hover:text-red-500"
                          onClick={() => removeCondition(id)}
                        />
                      </span>
                    );
                  })}
                </div>

                <div className="pt-4 flex justify-end border-t mt-5">
                  <CommonButton
                    text={isNetworkBusy ? "Saving..." : "Save & Continue"}
                    variant="filled"
                    className="px-6 py-2 shadow-md"
                    onClick={() => handleSaveStep(3)}
                    disabled={isNetworkBusy}
                  />
                </div>
              </CardWrapper>
            </div>
          )}
        </div>

        {/* --- SECTION 4: GROOMING --- */}
        <div ref={step4Ref} className="scroll-mt-6">
          {activeStepIndex === 4 && (
            <div className="mb-4 transition-all duration-300 animate-fadeIn space-y-4">
              {isNewPetRegistration && (
                <div className="bg-gradient-to-r from-amber-400/20 via-yellow-400/10 to-amber-400/20 border border-amber-300 rounded-2xl px-4 py-3 text-xs text-amber-950 font-black flex items-center gap-2 shadow-xs animate-pulse">
                  <Sparkles size={16} className="text-amber-600" /> Finalize
                  Coat Grooming & Shedding Interval behaviors to unlock the
                  grand 100 PTS jackpot! 🏆
                </div>
              )}

              <CardWrapper
                onClick={() => {}}
                title="4. Grooming & Shedding"
                isOpen={true}
              >
                <div className="mb-5">
                  <p className="text-md text-mainBrandColor mb-3 flex items-center gap-1.5">
                    <Layers size={14} /> Specify Breed Size
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {(["Small", "Medium", "Large", "Extra Large"] as const).map(
                      (size) => (
                        <button
                          type="button"
                          key={size}
                          onClick={() => setBreedSize(size)}
                          className={`py-2.5 rounded-xl border text-xs font-bold transition-all ${
                            breedSize === size
                              ? "bg-[#2F5E2E] text-white shadow-md scale-105"
                              : "bg-white text-gray-500"
                          }`}
                        >
                          {size}
                          <span className="text-[12px] font-medium block opacity-75 mt-0.5">
                            {size === "Small" && "< 10kg"}
                            {size === "Medium" && "10kg - 25kg"}
                            {size === "Large" && "25kg - 45kg"}
                            {size === "Extra Large" && "> 45kg"}
                          </span>
                        </button>
                      )
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                  <TabPills
                    label="Grooming Interval"
                    options={customGroomingOptions}
                    value={groomingfrequecy}
                    onChange={setGroomingfrequecy}
                  />
                  <TabPills
                    label="Shedding Tendency"
                    options={sheddingfrequecyOptions}
                    value={sheddingfrequecy}
                    onChange={setSheddingfrequecy}
                  />
                </div>

                {groomingfrequecy === "3" && (
                  <div className="mt-4 p-4 bg-emerald-50/40 border border-emerald-100 rounded-2xl max-w-sm shadow-xs">
                    <label className="text-xs font-bold text-emerald-800 mb-2 flex items-center gap-1.5">
                      <CalendarDays size={14} /> Set Custom Routine Duration
                    </label>
                    <div className="flex items-center bg-white border border-emerald-200 rounded-xl px-3 py-1.5 justify-between">
                      <input
                        type="number"
                        min="1"
                        max="12"
                        value={customGroomingMonths}
                        onChange={(e) =>
                          setCustomGroomingMonths(e.target.value)
                        }
                        className="w-full text-sm outline-none bg-transparent font-bold text-gray-700"
                      />
                      <span className="text-xs font-bold text-emerald-700 ml-2">
                        {parseInt(customGroomingMonths) === 1
                          ? "Month"
                          : "Months"}
                      </span>
                    </div>
                  </div>
                )}

                <div className="pt-4 flex justify-center border-t mt-5">
                  <CommonButton
                    text={
                      isNetworkBusy
                        ? "Finalizing Setup..."
                        : isNewPetRegistration
                        ? "Complete Profile & Unlock 100 Pts! 🏆"
                        : "Update Pet Profile Configuration"
                    }
                    variant="filled"
                    className="w-full md:w-[60%] py-3 font-black text-sm shadow-md"
                    onClick={() => handleSaveStep(4)}
                    disabled={isNetworkBusy}
                  />
                </div>
              </CardWrapper>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddPetProfileUI;
